import { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Container,
} from "@mui/material";
import { ToggleButtonGroup, ToggleButton } from "@mui/material";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import TrainingMaterialCard from "../TrainingComponents/TrainingMaterialCard";
import TrainingMaterialListCard from "../TrainingComponents/TrainingMaterialListCard";
import { GET_EMPLOYEE_TRAINING_MODULE_BY_ID } from "../../../apis/training-apis/trainingApis";
import { useQuery } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { ViewType } from "../utils/enums";
import { getIconByType } from "../utils/TrainingUtils";
import { GET_MASTER_LOOKUP_BY_TYPE } from "../../../apis/common-apis/commonApis";
import { MasterLookupItemsType } from "../../../utils/enums";

const TrainingModule = () => {
  const [searchParams] = useSearchParams();
  const queryId = searchParams.get("id");
  const moduleId = parseInt(queryId);
  const { moduleName } = useParams();
  const [viewType, setViewType] = useState(ViewType.LIST);

  const navigate = useNavigate();

  const handleViewChange = (_, nextView) => {
    if (nextView !== null) {
      setViewType(nextView);
    }
  };

  const handleOnClick = (event, section) => {
    let formattedTitle = section?.title?.replace(/\s+/g, "-").toLowerCase();
    navigate(`/training/${moduleName}/${formattedTitle}?id=${section?.id}`, {
      state: { moduleId },
    });
  };

  const { data: trainingDetailsData, loading } = useQuery(
    GET_EMPLOYEE_TRAINING_MODULE_BY_ID,
    {
      variables: { id: moduleId },
    }
  );

  const { data: masterLookup, loading: masterLookupLoading } = useQuery(
    GET_MASTER_LOOKUP_BY_TYPE,
    {
      variables: { type: MasterLookupItemsType.TRAINING_SECTION },
    }
  );

  if (loading || masterLookupLoading) {
    return (
      <Container
        component="main"
        maxWidth="xs"
        sx={{ textAlign: "center", mt: 8 }}
      >
        <CircularProgress />
      </Container>
    );
  }

  const trainingDetails =
    trainingDetailsData?.getEmployeeTrainingModuleById?.data;

  const masterLookupData = masterLookup?.getMasterLookupByType?.data ?? [];
  const trainingSectionMap = Object.fromEntries(
    masterLookupData?.map((item) => [item?.key, item?.title]) ?? []
  );

  return (
    <Box sx={{ padding: "0px" }}>
      {/* Training Title */}
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        {trainingDetails?.title}
      </Typography>

      {/* Description */}
      <Typography variant="body1" sx={{ color: "text.secondary", mb: 2 }}>
        {trainingDetails?.description}
      </Typography>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5" fontWeight="bold">
          Training Material ({trainingDetails?.total_sections ?? 0})
        </Typography>
        <ToggleButtonGroup
          value={viewType}
          exclusive
          onChange={handleViewChange}
          size="small"
        >
          <ToggleButton value={ViewType.LIST} aria-label="list view">
            <ViewListIcon />
          </ToggleButton>
          <ToggleButton value={ViewType.GRID} aria-label="grid view">
            <ViewModuleIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={3}>
        {trainingDetails?.sections?.map((section) => (
          <Grid
            item
            xs={12}
            sm={viewType === ViewType.GRID ? 6 : 12}
            md={viewType === ViewType.GRID ? 4 : 12}
            lg={viewType === ViewType.GRID ? 3 : 12}
            key={section?.id}
          >
            {viewType === ViewType.GRID ? (
              <TrainingMaterialCard
                section={section}
                getIconByType={getIconByType}
                onClick={handleOnClick}
                small={false}
                showCompletetionStaus={true}
                trainingSectionMap={trainingSectionMap}
              />
            ) : (
              <TrainingMaterialListCard
                section={section}
                getIconByType={getIconByType}
                onClick={handleOnClick}
                small={false}
                showCompletetionStaus={true}
                trainingSectionMap={trainingSectionMap}
              />
            )}
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default TrainingModule;
